// ─────────────────────────────────────────────────────────────
// AppShell — persistent layout with sidebar navigation.
// VS Code-inspired: thin sidebar with icons + labels,
// content area fills the rest.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, type ReactNode } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import type { PaletteMode } from "@mui/material";

export type ViewId = "home" | "backlog" | "kanban" | "jamie" | "history";

interface NavItem {
  id: ViewId;
  label: string;
  icon: ReactNode;
  tooltip: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: "home",
    label: "Home",
    icon: <HomeOutlinedIcon fontSize="small" />,
    tooltip: "Home — welcome and quick actions",
  },
  {
    id: "backlog",
    label: "Backlog",
    icon: <ListAltOutlinedIcon fontSize="small" />,
    tooltip: "Backlog — all feature cards by status",
  },
  {
    id: "kanban",
    label: "Sprint Board",
    icon: <ViewKanbanOutlinedIcon fontSize="small" />,
    tooltip: "Sprint Board — Kanban drag-and-drop",
  },
  {
    id: "jamie",
    label: "Jamie",
    icon: <SmartToyOutlinedIcon fontSize="small" />,
    tooltip: "Jamie — AI Product Owner chat",
  },
  {
    id: "history",
    label: "Sprints",
    icon: <HistoryOutlinedIcon fontSize="small" />,
    tooltip: "All sprints — planning, queued, active, and completed",
  },
];

const DRAWER_WIDTH = 192;

interface AppShellProps {
  activeView: ViewId;
  onNavigate: (view: ViewId) => void;
  colorMode: PaletteMode;
  onToggleColorMode: () => void;
  onMeetTheTeam: () => void;
  children: ReactNode;
  /** Optional extra nav item rendered at the bottom of the primary nav list */
  extraNavItem?: ReactNode;
}

export function AppShell({
  activeView,
  onNavigate,
  colorMode,
  onToggleColorMode,
  onMeetTheTeam,
  children,
  extraNavItem,
}: AppShellProps) {
  const [userName, setUserName] = useState<string>(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("auth_user") || "null");
      return authUser?.full_name ?? authUser?.email ?? "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === "auth_user") {
        try {
          const authUser = JSON.parse(e.newValue || "null");
          setUserName(authUser?.full_name ?? authUser?.email ?? "");
        } catch {
          setUserName("");
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      {/* ── Sidebar ─────────────────────────────────────────── */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Brand header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ display: "block", lineHeight: 1.2 }}
          >
            Maps Discover Studio
          </Typography>
          <Typography variant="subtitle1" noWrap>
            Dashboard
          </Typography>
        </Box>

        {/* Nav items */}
        <List sx={{ pt: 1, flex: 1 }}>
          {NAV_ITEMS.map((item) => (
            <Tooltip key={item.id} title={item.tooltip} placement="right" arrow>
              <ListItemButton
                selected={activeView === item.id}
                onClick={() => onNavigate(item.id)}
                sx={{ mb: 0.25 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItemButton>
            </Tooltip>
          ))}
        </List>

        {/* Extra nav item (e.g. cross-app link) */}
        {extraNavItem && (
          <Box sx={{ px: 1, pb: 1 }}>
            {extraNavItem}
          </Box>
        )}

        {/* Bottom controls */}
        <Box sx={{ p: 1, borderTop: "1px solid", borderColor: "divider" }}>
          {/* Meet the Team button */}
          <Tooltip title="Meet the AI agents behind this project" placement="right" arrow>
            <ListItemButton
              onClick={onMeetTheTeam}
              sx={{ borderRadius: 1, mb: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <PeopleOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Meet the Team"
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItemButton>
          </Tooltip>
          <Divider sx={{ mb: 1 }} />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {colorMode === "dark" ? "Dark" : "Light"} mode
            </Typography>
            <Tooltip title={`Switch to ${colorMode === "dark" ? "light" : "dark"} mode`}>
              <IconButton
                size="small"
                onClick={onToggleColorMode}
                aria-label="Toggle color mode"
              >
                {colorMode === "dark" ? (
                  <LightModeOutlinedIcon fontSize="small" />
                ) : (
                  <DarkModeOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>

          <Divider sx={{ my: 0.75 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              px: 1,
              opacity: 0.7,
            }}
          >
            <PersonOutlinedIcon sx={{ fontSize: "0.875rem", color: "text.secondary", flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {userName}
            </Typography>
          </Box>
        </Box>
      </Drawer>

      {/* ── Main content ─────────────────────────────────────── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
